import { Stack, Theme, Typography } from "@mui/material";
import { ScrollCards } from "../../../Common/components/ScrollCards";
import { useTranslation } from "react-i18next";
import { formatAmount } from "@/utils";
import {
  Vouchers,
  VoucherServiceTypes,
  VoucherType,
} from "@/services/core-api-adapter";
import Panel from "@/features/Common/components/Panel";
import theme from "@/theme";
import IconLoader from "@/features/Common/components/IconLoader";

interface VoucherBottomPanelsProps {
  item: any;
  index: number;
}

function VoucherBottomPanel({ item, index }: VoucherBottomPanelsProps) {
  return (
    <Panel key={index}>
      <Stack
        component="button"
        aria-label={item.title}
        onClick={item.onClick}
        sx={{
          cursor: "pointer",
          border: "none",
          background: "transparent",
          minWidth: "100%",
        }}
      >
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography textAlign="left" variant="h4" fontWeight={600}>
            {item.title}
          </Typography>
          <Typography textAlign="left" variant="body1" color="neutral.light">
            {item.description}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Panel
              sx={{
                boxShadow: "none",
                height: "fit-content",
                backgroundColor: (theme: Theme) => theme.palette.primary.main,
              }}
            >
              <Typography fontWeight={600} color="info">
                {item.buttonText}
              </Typography>
            </Panel>
            <IconLoader
              icon="ChevronNextIcon"
              sx={{ color: theme.palette.neutral.light }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Panel>
  );
}

interface BuyVoucherTabProps {
  voucher: Vouchers | undefined;
  onDoctorNurseConsultationVoucherClicked: (voucher: VoucherType) => void;
  onPathologyTestVoucherClicked: (voucher: VoucherType) => void;
}

export default function BuyVoucherTab({
  voucher,
  onDoctorNurseConsultationVoucherClicked,
  onPathologyTestVoucherClicked,
}: BuyVoucherTabProps) {
  const { t } = useTranslation();

  const doctorAmountFormatted = voucher?.voucherTypes
    ?.find((item) => item.voucherTypeId === VoucherServiceTypes.CONSULTATION)
    ?.voucherSubTypes?.slice()
    .sort((a, b) => {
      if (b.discountAmount !== a.discountAmount) {
        return b.discountAmount - a.discountAmount;
      }
      return a.amount - b.amount;
    })[0]?.amount;

  const bloodTestAmountFormatted = voucher?.voucherTypes
    ?.find((item) => item.voucherTypeId === VoucherServiceTypes.PATHOLOGY)
    ?.voucherSubTypes?.slice()
    .sort((a, b) => {
      if (b.discountAmount !== a.discountAmount) {
        return b.discountAmount - a.discountAmount;
      }
      return a.amount - b.amount;
    })[0]?.amount;

  const currencyCode = voucher?.voucherTypes?.find(
    (item) => item.voucherTypeId === VoucherServiceTypes.CONSULTATION
  )?.voucherSubTypes?.[0]?.currencyCode;

  function voucherTypeId(voucherId: VoucherServiceTypes): VoucherType {
    const voucherType = voucher?.voucherTypes?.find(
      (item) => item.voucherTypeId === voucherId
    );
    if (!voucherType) {
      throw new Error(`VoucherType with id ${voucherId} not found`);
    }
    return voucherType;
  }

  const buyVoucherCardData = [
    {
      title: t("AddOrUseVoucher.Voucher.BuyVoucherTab.cardOne.title"),
      description: t(
        "AddOrUseVoucher.Voucher.BuyVoucherTab.cardOne.description"
      ),
      iconName: "VoucherTypeIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.BuyVoucherTab.cardTwo.title"),
      description: t(
        "AddOrUseVoucher.Voucher.BuyVoucherTab.cardTwo.description"
      ),
      iconName: "VoucherPaymentIcon",
    },
    {
      title: t("AddOrUseVoucher.Voucher.BuyVoucherTab.cardThree.title"),
      description: t(
        "AddOrUseVoucher.Voucher.BuyVoucherTab.cardThree.description"
      ),
      iconName: "VoucherShareIcon",
    },
  ];

  const buyVoucherBottomItems = [
    {
      title: t("AddOrUseVoucher.Voucher.BuyVoucherTab.panelOne.title"),
      description: t(
        "AddOrUseVoucher.Voucher.BuyVoucherTab.panelOne.description"
      ),
      buttonText: t("AddOrUseVoucher.Voucher.BuyVoucherTab.panelOne.price", {
        priceFormatted: formatAmount(
          doctorAmountFormatted || 0,
          currencyCode || ""
        ),
      }),
      onClick: () =>
        onDoctorNurseConsultationVoucherClicked(
          voucherTypeId(VoucherServiceTypes.CONSULTATION)
        ),
    },
    {
      title: t("AddOrUseVoucher.Voucher.BuyVoucherTab.panelTwo.title"),
      description: t(
        "AddOrUseVoucher.Voucher.BuyVoucherTab.panelTwo.description"
      ),
      buttonText: t("AddOrUseVoucher.Voucher.BuyVoucherTab.panelTwo.price", {
        priceFormatted: formatAmount(
          bloodTestAmountFormatted || 0,
          currencyCode || ""
        ),
      }),
      onClick: () =>
        onPathologyTestVoucherClicked(
          voucherTypeId(VoucherServiceTypes.PATHOLOGY)
        ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h4" fontWeight={600}>
        {t("AddOrUseVoucher.Voucher.BuyVoucherPage.title")}
      </Typography>

      <ScrollCards cardsData={buyVoucherCardData} />

      <Stack spacing={3} display="flex" flexDirection="column">
        {buyVoucherBottomItems.map((item, index) => (
          <VoucherBottomPanel key={index} item={item} index={index} />
        ))}
      </Stack>
    </Stack>
  );
}
